import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Stack, Modal, Button, Flex, Input, Select, useTheme, spacing } from '@fti/design-system';
import type { DatasetWithPreview } from '../../types/dataset';
import type { ColumnType } from '../../types/columnType';
import { COLUMN_TYPES } from '../../types/columnType';
import type { DatasetRowData } from '../../types/dataGrid';
import { inferColumnTypes, extractColumnsFromRows } from '../../utils/typeInference';
import { checkCompatibility } from '../../utils/typeValidation';
import { IncompatibilityWarning } from '../ColumnTypeSystem/IncompatibilityWarning';
import { useDraftState } from './hooks/useDraftState';
import { useDataPagination } from './hooks/useDataPagination';
import { useWorkspaceLock } from './hooks/useWorkspaceLock';
import { DataGridToolbar } from './DataGridToolbar';
import { DataGridHeader } from './DataGridHeader';
import { DataGridRow } from './DataGridRow';
import { saveDatasetEdits } from '../../services/dataGridApi';
import { saveColumnTypes } from '../../services/datasetApi';

interface DataGridProps {
  dataset: DatasetWithPreview;
  onSaveComplete?: () => void;
  onDraftStateChange?: (hasDrafts: boolean) => void;
  toolbarRightContent?: React.ReactNode;
}

export const DataGrid: React.FC<DataGridProps> = ({
  dataset,
  onSaveComplete,
  onDraftStateChange,
  toolbarRightContent,
}) => {
  const { theme } = useTheme();

  const draft = useDraftState(dataset.column_count);

  // Seed pagination with preview_rows to avoid waiting for an API fetch on mount
  const paginationOptions = useMemo(() => ({
    initialRows: dataset.preview_rows.map((r) => ({ row_index: r.row_index, values: r.values })),
    initialTotalRows: dataset.row_count,
  }), [dataset.preview_rows, dataset.row_count]);
  const pagination = useDataPagination(dataset.id, paginationOptions);

  const lock = useWorkspaceLock(dataset.workspace_id);

  const [activeCell, setActiveCell] = useState<{ row: number; col: number } | null>(null);
  const [editingCell, setEditingCell] = useState<{ row: number; col: number } | null>(null);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [selectedColumns, setSelectedColumns] = useState<Set<number>>(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [typeOverrides, setTypeOverrides] = useState<Map<number, ColumnType>>(new Map());

  // Modals
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [showDeleteRowModal, setShowDeleteRowModal] = useState(false);
  const [showDeleteColModal, setShowDeleteColModal] = useState(false);
  const [showAddColModal, setShowAddColModal] = useState(false);
  const [newColName, setNewColName] = useState('');
  const [newColType, setNewColType] = useState<ColumnType>('Text');
  const [colNameError, setColNameError] = useState<string | null>(null);

  // Pagination state
  const GRID_PAGE_SIZE = 30;
  const [gridPage, setGridPage] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // Notify parent of draft state changes
  useEffect(() => {
    onDraftStateChange?.(draft.hasDraftChanges);
  }, [draft.hasDraftChanges, onDraftStateChange]);

  // Keyboard shortcuts for undo/redo (only when modal is NOT open)
  // Note: pendingTypeChange is declared further down, so we check it in the effect body
  useEffect(() => {
    if (showDiscardModal || showDeleteRowModal || showDeleteColModal || showAddColModal) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      const isMeta = e.metaKey || e.ctrlKey;
      if (isMeta && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        draft.undo();
      } else if (isMeta && (e.key === 'Z' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        draft.redo();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [draft, showDiscardModal, showDeleteRowModal, showDeleteColModal, showAddColModal]);

  // Unsaved changes warning on page unload
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (draft.hasDraftChanges) {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [draft.hasDraftChanges]);

  // Effective columns: original + added
  const effectiveColumns = useMemo(() => {
    return [...dataset.columns, ...draft.columnsAdded.map((c) => c.name)];
  }, [dataset.columns, draft.columnsAdded]);

  // Column types: original + added types (same inference logic as DataPreview)
  const effectiveColumnTypes = useMemo<ColumnType[]>(() => {
    let original: ColumnType[];
    if (dataset.column_types && dataset.column_types.length > 0) {
      original = dataset.column_types.map((ct) => ct.column_type);
    } else {
      // Fall back to inference from preview data (same as DataPreview)
      const columnData = extractColumnsFromRows(dataset.preview_rows, dataset.columns.length);
      const inferred = inferColumnTypes(columnData, dataset.columns);
      original = inferred;
    }
    // Apply user overrides
    typeOverrides.forEach((type, idx) => {
      if (idx < original.length) {
        original[idx] = type;
      }
    });
    const added = draft.columnsAdded.map((c) => c.type);
    return [...original, ...added];
  }, [dataset.column_types, dataset.columns, dataset.preview_rows, draft.columnsAdded, typeOverrides]);

  // Total row count including added rows
  const totalRowCount = useMemo(() => {
    const original = pagination.totalRows || dataset.row_count;
    return original + draft.rowsAdded.length;
  }, [pagination.totalRows, dataset.row_count, draft.rowsAdded.length]);

  // Paginated rows
  const gridTotalPages = Math.ceil(totalRowCount / GRID_PAGE_SIZE);

  const renderedRows = useMemo(() => {
    const start = gridPage * GRID_PAGE_SIZE;
    const end = Math.min(totalRowCount, start + GRID_PAGE_SIZE);
    const originalRowCount = pagination.totalRows || dataset.row_count;

    const rows: { rowIndex: number; data: DatasetRowData | null; isNew: boolean }[] = [];
    for (let i = start; i < end; i++) {
      if (i < originalRowCount) {
        const row = pagination.getRow(i);
        rows.push({ rowIndex: i, data: row, isNew: false });
      } else {
        const addedIdx = i - originalRowCount;
        const addedRow = draft.rowsAdded[addedIdx];
        if (addedRow) {
          rows.push({ rowIndex: i, data: { row_index: i, values: addedRow.values }, isNew: true });
        }
      }
    }

    return rows;
  }, [gridPage, totalRowCount, pagination.totalRows, dataset.row_count, pagination.getRow, draft.rowsAdded]);

  // Ensure current page rows are loaded from backend
  useEffect(() => {
    const originalRowCount = pagination.totalRows || dataset.row_count;
    const start = gridPage * GRID_PAGE_SIZE;
    const end = Math.min(originalRowCount, start + GRID_PAGE_SIZE);
    if (start < originalRowCount) {
      pagination.ensureRowsLoaded(start, end);
    }
  }, [gridPage, pagination.totalRows, dataset.row_count, pagination.ensureRowsLoaded]);

  // Acquire lock on first edit (best-effort — don't block editing if lock fails)
  const ensureLock = useCallback(async () => {
    if (!lock.isLocked && !lock.acquiring) {
      try {
        await lock.acquire();
      } catch {
        // Lock failed — user can still edit, just without multi-user protection
      }
    }
  }, [lock.isLocked, lock.acquiring, lock.acquire]);

  // Cell activation (single click — no edit mode)
  const handleActivate = useCallback((rowIndex: number, columnIndex: number) => {
    setActiveCell({ row: rowIndex, col: columnIndex });
    setEditingCell(null);
  }, []);

  // Cell editing (double-click or Enter/F2)
  const handleStartEdit = useCallback(
    async (rowIndex: number, columnIndex: number) => {
      await ensureLock();
      setActiveCell({ row: rowIndex, col: columnIndex });
      setEditingCell({ row: rowIndex, col: columnIndex });
    },
    [ensureLock]
  );

  const handleCommitEdit = useCallback(
    (rowIndex: number, columnIndex: number, value: string | number | null) => {
      const row = pagination.getRow(rowIndex);
      const previousValue = row ? row.values[columnIndex] : null;
      if (value !== previousValue) {
        draft.editCell(rowIndex, columnIndex, value, previousValue);
      }
      setEditingCell(null);
    },
    [draft, pagination.getRow]
  );

  const handleCancelEdit = useCallback(() => {
    setEditingCell(null);
  }, []);

  // Navigation
  const handleNavigate = useCallback(
    (direction: 'up' | 'down' | 'left' | 'right') => {
      if (!activeCell) return;
      let { row, col } = activeCell;

      switch (direction) {
        case 'up': row = Math.max(0, row - 1); break;
        case 'down': row = Math.min(totalRowCount - 1, row + 1); break;
        case 'left': col = Math.max(0, col - 1); break;
        case 'right': col = Math.min(effectiveColumns.length - 1, col + 1); break;
      }

      setActiveCell({ row, col });
      setEditingCell(null);
    },
    [activeCell, totalRowCount, effectiveColumns.length]
  );

  // Column type change with compatibility check
  const [pendingTypeChange, setPendingTypeChange] = useState<{
    columnIndex: number;
    targetType: ColumnType;
    incompatibleCount: number;
    totalCount: number;
  } | null>(null);

  const applyTypeChange = useCallback((columnIndex: number, newType: ColumnType) => {
    setTypeOverrides((prev) => {
      const next = new Map(prev);
      next.set(columnIndex, newType);
      return next;
    });
  }, []);

  const handleTypeChange = useCallback((columnIndex: number, newType: ColumnType) => {
    if (newType === effectiveColumnTypes[columnIndex]) return;

    const columnData = extractColumnsFromRows(dataset.preview_rows, dataset.columns.length);
    if (columnIndex < columnData.length) {
      const result = checkCompatibility(columnData[columnIndex], newType);
      if (result.incompatible > 0) {
        setPendingTypeChange({
          columnIndex,
          targetType: newType,
          incompatibleCount: result.incompatible,
          totalCount: result.total,
        });
        return;
      }
    }
    applyTypeChange(columnIndex, newType);
  }, [effectiveColumnTypes, dataset.preview_rows, dataset.columns.length, applyTypeChange]);

  const handleConfirmTypeChange = useCallback(() => {
    if (pendingTypeChange) {
      applyTypeChange(pendingTypeChange.columnIndex, pendingTypeChange.targetType);
      setPendingTypeChange(null);
    }
  }, [pendingTypeChange, applyTypeChange]);

  // Row selection (clears column selection)
  const handleSelectRow = useCallback((rowIndex: number, selected: boolean) => {
    setSelectedColumns(new Set());
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (selected) next.add(rowIndex);
      else next.delete(rowIndex);
      return next;
    });
  }, []);

  // Column selection (clears row selection)
  const handleSelectColumn = useCallback((columnIndex: number, selected: boolean) => {
    setSelectedRows(new Set());
    setSelectedColumns((prev) => {
      const next = new Set(prev);
      if (selected) next.add(columnIndex);
      else next.delete(columnIndex);
      return next;
    });
  }, []);

  // Add row
  const handleAddRow = useCallback(async () => {
    await ensureLock();
    draft.addRow();
  }, [ensureLock, draft]);

  // Delete rows (with confirmation)
  const handleConfirmDeleteRows = useCallback(() => {
    draft.deleteRows(Array.from(selectedRows));
    setSelectedRows(new Set());
    setShowDeleteRowModal(false);
  }, [draft, selectedRows]);

  // Add column
  const handleConfirmAddColumn = useCallback(() => {
    const trimmed = newColName.trim();
    if (!trimmed) {
      setColNameError('Column name is required');
      return;
    }
    const existingNames = dataset.columns.map((c, i) => draft.getColumnName(i, c));
    const addedNames = draft.columnsAdded.map((c) => c.name);
    if ([...existingNames, ...addedNames].includes(trimmed)) {
      setColNameError('Column name already exists');
      return;
    }
    draft.addColumn(trimmed, newColType, pagination.totalRows);
    setShowAddColModal(false);
    setNewColName('');
    setNewColType('Text');
    setColNameError(null);
  }, [newColName, newColType, dataset.columns, draft, pagination.totalRows]);

  // Delete columns (with confirmation)
  const handleConfirmDeleteColumns = useCallback(() => {
    if (selectedColumns.size > 0) {
      draft.deleteColumns(Array.from(selectedColumns));
      setSelectedColumns(new Set());
    }
    setShowDeleteColModal(false);
  }, [draft, selectedColumns]);

  // Save
  const handleSave = useCallback(async () => {
    setIsSaving(true);
    setSaveError(null);
    try {
      // Save data edits if any
      if (draft.hasDraftChanges) {
        const changeset = draft.buildChangeset();
        await saveDatasetEdits(dataset.id, changeset);
      }

      // Save type changes if any
      if (typeOverrides.size > 0) {
        const columns = Array.from(typeOverrides.entries()).map(([column_index, column_type]) => ({
          column_index,
          column_type,
        }));
        await saveColumnTypes(dataset.id, columns);
      }

      draft.clearAfterSave();
      setTypeOverrides(new Map());
      setSelectedRows(new Set());
      setSelectedColumns(new Set());
      lock.release().catch(() => {}); // Best effort
      pagination.reload();
      onSaveComplete?.();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setIsSaving(false);
    }
  }, [draft, dataset.id, lock, pagination.reload, onSaveComplete, typeOverrides]);

  // Discard
  const handleConfirmDiscard = useCallback(async () => {
    draft.discardAll();
    setTypeOverrides(new Map());
    setSelectedRows(new Set());
    setSelectedColumns(new Set());
    setShowDiscardModal(false);
    await lock.release();
  }, [draft, lock]);

  const isColumnRenamed = useCallback(
    (idx: number) => {
      if (idx >= dataset.columns.length) return false;
      return draft.getColumnName(idx, dataset.columns[idx]) !== dataset.columns[idx];
    },
    [draft, dataset.columns]
  );

  return (
    <Stack gap="sm">
      <DataGridToolbar
        hasDraftChanges={draft.hasDraftChanges || typeOverrides.size > 0}
        canUndo={draft.canUndo}
        canRedo={draft.canRedo}
        selectedRowCount={selectedRows.size}
        selectedColumnCount={selectedColumns.size}
        isSaving={isSaving}
        onSave={handleSave}
        onDiscard={() => setShowDiscardModal(true)}
        onUndo={draft.undo}
        onRedo={draft.redo}
        onAddRow={handleAddRow}
        onDeleteRows={() => setShowDeleteRowModal(true)}
        onAddColumn={() => setShowAddColModal(true)}
        onDeleteColumns={() => setShowDeleteColModal(true)}
        rightContent={toolbarRightContent}
      />

      {saveError && (
        <div
          style={{
            padding: spacing.sm,
            borderRadius: '6px',
            backgroundColor: `${theme.colors.semantic.error.light}20`,
            border: `1px solid ${theme.colors.semantic.error.light}`,
            fontSize: '0.85rem',
            color: theme.colors.semantic.error.default,
          }}
        >
          {saveError}
          <Button variant="ghost" size="sm" onClick={handleSave} style={{ marginLeft: spacing.sm }}>
            Retry
          </Button>
        </div>
      )}

      {lock.error && (
        <div
          style={{
            padding: spacing.sm,
            borderRadius: '6px',
            backgroundColor: `${theme.colors.semantic.warning.light}20`,
            border: `1px solid ${theme.colors.semantic.warning.light}`,
            fontSize: '0.85rem',
            color: theme.colors.semantic.warning.default,
          }}
        >
          {lock.error}
        </div>
      )}

      <div
        ref={containerRef}
        style={{
          width: '100%',
          overflowX: 'auto',
        }}
      >
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontFamily: 'inherit',
            fontSize: '1rem',
            color: theme.colors.text.primary,
          }}
          role="grid"
          aria-label="Interactive data grid"
        >
          <DataGridHeader
            columns={effectiveColumns}
            columnTypes={effectiveColumnTypes}
            isColumnDeleted={draft.isColumnDeleted}
            getColumnName={draft.getColumnName}
            isColumnRenamed={isColumnRenamed}
            onRenameColumn={draft.renameColumn}
            onTypeChange={handleTypeChange}
            allColumns={effectiveColumns}
            selectedColumns={selectedColumns}
            onSelectColumn={handleSelectColumn}
          />
          <tbody>
            {renderedRows.map(({ rowIndex, data, isNew }) => {
              if (!data) {
                return (
                  <tr key={rowIndex} style={{ height: '36px' }}>
                    <td
                      colSpan={effectiveColumns.length + 1}
                      style={{ padding: spacing.sm, color: theme.colors.text.secondary, textAlign: 'center' }}
                    >
                      Loading...
                    </td>
                  </tr>
                );
              }

              return (
                <DataGridRow
                  key={rowIndex}
                  rowIndex={rowIndex}
                  values={data.values}
                  columns={effectiveColumns}
                  isDeleted={draft.isRowDeleted(rowIndex)}
                  isSelected={selectedRows.has(rowIndex)}
                  isNewRow={isNew}
                  activeCell={activeCell}
                  editingCell={editingCell}
                  isCellDirty={draft.isCellDirty}
                  getCellValue={draft.getCellValue}
                  isColumnDeleted={draft.isColumnDeleted}
                  onSelectRow={handleSelectRow}
                  onActivate={handleActivate}
                  onStartEdit={handleStartEdit}
                  onCommitEdit={handleCommitEdit}
                  onCancelEdit={handleCancelEdit}
                  onNavigate={handleNavigate}
                />
              );
            })}
          </tbody>
        </table>
      </div>

      <Flex justify="between" align="center">
        <p style={{ margin: 0, fontSize: '0.85rem', color: theme.colors.text.secondary }}>
          Showing rows {gridPage * GRID_PAGE_SIZE + 1}–{Math.min((gridPage + 1) * GRID_PAGE_SIZE, totalRowCount)} of {totalRowCount.toLocaleString()}
          {draft.rowsAdded.length > 0 && ` (${draft.rowsAdded.length} added)`}
        </p>
        {gridTotalPages > 1 && (
          <Flex gap="xs" align="center">
            <Button
              variant="outline"
              color="neutral"
              size="sm"
              disabled={gridPage === 0}
              onClick={() => setGridPage((p) => p - 1)}
            >
              &lt;
            </Button>
            {(() => {
              const pages: (number | 'ellipsis')[] = [];
              if (gridTotalPages <= 7) {
                for (let i = 0; i < gridTotalPages; i++) pages.push(i);
              } else {
                pages.push(0);
                if (gridPage > 2) pages.push('ellipsis');
                const start = Math.max(1, gridPage - 1);
                const end = Math.min(gridTotalPages - 2, gridPage + 1);
                for (let i = start; i <= end; i++) pages.push(i);
                if (gridPage < gridTotalPages - 3) pages.push('ellipsis');
                pages.push(gridTotalPages - 1);
              }
              return pages.map((page, idx) =>
                page === 'ellipsis' ? (
                  <span key={`ellipsis-${idx}`} style={{ fontSize: '0.85rem', color: theme.colors.text.secondary, padding: `0 ${spacing.xs}` }}>
                    …
                  </span>
                ) : (
                  <Button
                    key={page}
                    variant={page === gridPage ? 'solid' : 'outline'}
                    color={page === gridPage ? 'primary' : 'neutral'}
                    size="sm"
                    onClick={() => setGridPage(page)}
                    style={{ minWidth: '32px' }}
                  >
                    {page + 1}
                  </Button>
                )
              );
            })()}
            <Button
              variant="outline"
              color="neutral"
              size="sm"
              disabled={gridPage >= gridTotalPages - 1}
              onClick={() => setGridPage((p) => p + 1)}
            >
              &gt;
            </Button>
          </Flex>
        )}
      </Flex>

      {/* Discard All Confirmation Modal */}
      <Modal isOpen={showDiscardModal} onClose={() => setShowDiscardModal(false)}>
        <Stack gap="md" style={{ padding: spacing.lg }}>
          <h3 style={{ margin: 0 }}>Discard All Changes?</h3>
          <p style={{ margin: 0, color: theme.colors.text.secondary }}>
            All unsaved edits will be lost. This cannot be undone.
          </p>
          <Flex gap="sm" justify="end">
            <Button variant="outline" color="neutral" onClick={() => setShowDiscardModal(false)}>
              Cancel
            </Button>
            <Button variant="solid" color="error" onClick={handleConfirmDiscard}>
              Discard All
            </Button>
          </Flex>
        </Stack>
      </Modal>

      {/* Delete Rows Confirmation Modal */}
      <Modal isOpen={showDeleteRowModal} onClose={() => setShowDeleteRowModal(false)}>
        <Stack gap="md" style={{ padding: spacing.lg }}>
          <h3 style={{ margin: 0 }}>Delete {selectedRows.size} Row{selectedRows.size > 1 ? 's' : ''}?</h3>
          <p style={{ margin: 0, color: theme.colors.text.secondary }}>
            Selected rows will be marked for deletion. Changes are saved when you click Save.
          </p>
          <Flex gap="sm" justify="end">
            <Button variant="outline" color="neutral" onClick={() => setShowDeleteRowModal(false)}>
              Cancel
            </Button>
            <Button variant="solid" color="error" onClick={handleConfirmDeleteRows}>
              Delete
            </Button>
          </Flex>
        </Stack>
      </Modal>

      {/* Delete Column Confirmation Modal */}
      <Modal isOpen={showDeleteColModal} onClose={() => setShowDeleteColModal(false)}>
        <Stack gap="md" style={{ padding: spacing.lg }}>
          <h3 style={{ margin: 0 }}>Delete Column?</h3>
          <p style={{ margin: 0, color: theme.colors.text.secondary }}>
            This column will be marked for deletion. Any chart configurations using this column may be affected.
          </p>
          <Flex gap="sm" justify="end">
            <Button variant="outline" color="neutral" onClick={() => setShowDeleteColModal(false)}>
              Cancel
            </Button>
            <Button variant="solid" color="error" onClick={handleConfirmDeleteColumns}>
              Delete Column
            </Button>
          </Flex>
        </Stack>
      </Modal>

      {/* Add Column Modal */}
      <Modal isOpen={showAddColModal} onClose={() => { setShowAddColModal(false); setColNameError(null); }}>
        <Stack gap="md" style={{ padding: spacing.lg }}>
          <h3 style={{ margin: 0 }}>Add Column</h3>
          <Stack gap="sm">
            <label style={{ fontSize: '0.85rem', fontWeight: 500 }}>Column Name</label>
            <Input
              value={newColName}
              onChange={(e) => { setNewColName(e.target.value); setColNameError(null); }}
              placeholder="Enter column name"
              autoFocus
            />
            {colNameError && (
              <span style={{ fontSize: '0.75rem', color: theme.colors.semantic.error.default }}>
                {colNameError}
              </span>
            )}
          </Stack>
          <Stack gap="sm">
            <label style={{ fontSize: '0.85rem', fontWeight: 500 }}>Column Type</label>
            <Select
              value={newColType}
              onChange={(e) => setNewColType(e.target.value as ColumnType)}
              options={COLUMN_TYPES.map((t) => ({ value: t, label: t }))}
            />
          </Stack>
          <Flex gap="sm" justify="end">
            <Button variant="outline" color="neutral" onClick={() => { setShowAddColModal(false); setColNameError(null); }}>
              Cancel
            </Button>
            <Button variant="solid" color="primary" onClick={handleConfirmAddColumn}>
              Add Column
            </Button>
          </Flex>
        </Stack>
      </Modal>

      {/* Incompatibility Warning */}
      {pendingTypeChange && (
        <IncompatibilityWarning
          incompatibleCount={pendingTypeChange.incompatibleCount}
          totalCount={pendingTypeChange.totalCount}
          targetType={pendingTypeChange.targetType}
          onConfirm={handleConfirmTypeChange}
          onCancel={() => setPendingTypeChange(null)}
        />
      )}
    </Stack>
  );
};
